import { Tooltip, mergeClasses } from "@fluentui/react-components";
import { Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import { exportToExcel, removeUserFromGroups } from "api";
import { Constants, ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ChevronIcon } from "icons/ChevronIcon";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedAdminCellStyles,
} from "theme/sharedStyles";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { IUser } from "models/IUser";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { updateUser } from "../../redux/users";
import { useManageUsersStyles } from "./ManageUsers.styles";
import { EditDialog } from "./editDialog/EditDialog";

const ROLE_FILTER_OPTIONS: { key: string; label: string }[] = [
  { key: "__ALL__", label: "All elevated" },
  { key: Constants.ADMINS_GROUP_NAME_KEY, label: "Admin" },
  { key: Constants.PLATFORM_ADMINS_GROUP_NAME_KEY, label: "Platform Admin" },
  { key: Constants.DECOODA_ADMINS_GROUP_NAME_KEY, label: "Decooda Admin" },
  {
    key: Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_KEY,
    label: "Diagnostics Admin",
  },
  { key: Constants.REPORTING_GROUP_NAME_KEY, label: "Reporting" },
  { key: Constants.AI_METRICS_USERS_GROUP_NAME_KEY, label: "AI Metrics User" },
  {
    key: Constants.AI_METRICS_ADMINS_GROUP_NAME_KEY,
    label: "AI Metrics Admin",
  },
  {
    key: Constants.PROJECT_IQ_ADMINS_GROUP_NAME_KEY,
    label: "Project IQ Admin",
  },
];

export const ManageUsers = (): JSX.Element => {
  const cellStyles = useManageUsersStyles();
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const dispatch = useDispatch();
  const currentUser: any = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const userPrincipalName: string =
    currentUser?.userPrincipalName || currentUser.upn;

  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingUser, setDeletingUser] = useState<IUser | null>(null);
  const [isExportingToExcel, setIsExportingToExcel] = useState<boolean>(false);
  const [exportMessage, setExportMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Users",
    filterKeys: ["role"] as const,
  });

  const NON_ELEVATED = new Set(["user", "diagnosticsUser", "decoodaUser"]);
  const usersWithGroups: IUser[] = useMemo(
    () =>
      users.filter(
        (user: IUser) =>
          user.groups && user.groups.some((g) => !NON_ELEVATED.has(g)),
      ),
    [users],
  );

  const columns: ITableColumn<IUser>[] = useMemo(
    () => [
      {
        columnId: "displayName",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        renderCell: (item: IUser) => (
          <Tooltip content={item.displayName} relationship="label">
            <span className={adminCellStyles.nameText} title={item.displayName}>
              {item.displayName}
            </span>
          </Tooltip>
        ),
        compare: (a: IUser, b: IUser) =>
          a.displayName.localeCompare(b.displayName),
      },
      {
        columnId: "mail",
        displayValue: "Email",
        className: colStyles.normal,
        renderHeaderCell: () => "Email",
        renderCell: (item: IUser) => (
          <Tooltip content={item.mail} relationship="label">
            <span title={item.mail}>{item.mail}</span>
          </Tooltip>
        ),
        compare: (a: IUser, b: IUser) => a.mail.localeCompare(b.mail),
      },
      {
        columnId: "groups",
        displayValue: "Access",
        className: colStyles.wide,
        renderHeaderCell: () => "Access",
        renderCell: (item: IUser) => {
          const groupsText =
            item.groups?.filter((g) => !NON_ELEVATED.has(g)).join(", ") ||
            "No group";
          return (
            <Tooltip content={groupsText} relationship="label">
              <span className={cellStyles.groupsText} title={groupsText}>
                {groupsText}
              </span>
            </Tooltip>
          );
        },
        compare: (a: IUser, b: IUser) => {
          const groupA: string =
            a.groups?.filter((g) => !NON_ELEVATED.has(g)).join(", ") ||
            ConstantValues.EMPTY_STRING;
          const groupB: string =
            b.groups?.filter((g) => !NON_ELEVATED.has(g)).join(", ") ||
            ConstantValues.EMPTY_STRING;
          return groupA.localeCompare(groupB);
        },
      },
    ],
    [cellStyles, colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    onSortColumnChanged,
    onClearColumnChanged,
    sortItems,
  } = useAdminListSort<IUser>({
    cacheKey: "Users",
    defaultSortColumn: "displayName",
    defaultDescending: false,
  });

  const searchFields = useMemo(
    () => ["displayName", "mail"] as (keyof IUser)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IUser>({
    items: usersWithGroups,
    searchFields,
  });

  const fullyFilteredItems = useMemo(() => {
    if (!filters.role) return filteredItems;
    return filteredItems.filter((u) => u.groups?.includes(filters.role));
  }, [filteredItems, filters.role]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters = internalSearchQuery || filters.role;
    return hasActiveFilters
      ? "No users match the current filters."
      : "No users found with elevated privileges.";
  }, [internalSearchQuery, filters.role]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IUser>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const isExporting = isExportingToExcel;
  const isLoading = false;

  const handleAddUser = () => {
    setEditingUser(null);
    setIsEditDialogOpen(true);
  };

  const handleEditUser = (user: IUser) => {
    setEditingUser(user);
    setIsEditDialogOpen(true);
  };

  const handleDeleteUser = (user: IUser) => {
    setDeletingUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    try {
      await removeUserFromGroups(deletingUser.id);
      dispatch(updateUser({ ...deletingUser, groups: [] }));
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingUser(null);
    }
  };

  const handleExportToExcel = async (): Promise<void> => {
    setIsExportingToExcel(true);
    setExportMessage("Exporting to Excel...");
    try {
      await exportToExcel();
      setExportMessage("Exported to Excel successfully!");
      setTimeout(() => {
        setIsExportingToExcel(false);
        setExportMessage(ConstantValues.EMPTY_STRING);
      }, 1000);
    } catch (error) {
      console.error("Failed to export to Excel:", error);
      setIsExportingToExcel(false);
    }
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingUser(null);
  };

  const handleEditSuccess = (updatedUser: IUser) => {
    dispatch(updateUser(updatedUser));
  };

  const rowActions: AdminRowAction<IUser>[] = useMemo(
    () => [
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditUser,
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteUser,
        disabled: (user: IUser) => {
          const isAdmin = user.groups?.includes(
            Constants.ADMINS_GROUP_NAME_KEY,
          );
          const isCurrentUser = user.userPrincipalName === userPrincipalName;
          return isAdmin || isCurrentUser;
        },
      },
    ],
    [userPrincipalName],
  );

  const sidebarItem = findSidebarItem("users");
  const headerTitle = sidebarItem?.label || "Elevated Users";

  const roleOptions: { key: string; text: string }[] =
    ROLE_FILTER_OPTIONS.filter((opt) => opt.key !== "__ALL__").map((opt) => ({
      key: opt.key,
      text: opt.label,
    }));

  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>{headerTitle}</h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExportToExcel}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleAddUser}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search by name or email..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search by name or email..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.role}
              onChange={(e) => setFilter("role", e.target.value)}
              aria-label="All Access Types"
            >
              <option value="">All Access Types</option>
              {roleOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>

      <AdminListPage<IUser>
        title="Manage Users"
        items={usersWithGroups}
        columns={columns}
        isLoading={isLoading || isExporting}
        headerActions={[]}
        searchPlaceholder="Search by name or email..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(user) => user.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isEditDialogOpen && (
        <EditDialog
          users={users}
          user={editingUser || undefined}
          isOpen={isEditDialogOpen}
          onDismiss={() => {
            handleCloseEditDialog();
          }}
          onUpdateUser={handleEditSuccess}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete User"
        message={`Are you sure you want to remove "${deletingUser?.displayName}" from all groups? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        spinnerMessage="Removing user..."
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingUser(null);
        }}
      />

      {isExportingToExcel && (
        <ActionDialog
          isVisible={isExportingToExcel}
          title="Export to Excel"
          message="Are you sure you want to export users to Excel?"
          confirmationMessage="Yes"
          dismissMessage="No"
          hasSpinner={exportMessage === "Exporting to Excel..."}
          spinnerMessage={exportMessage}
          onConfirmation={async () => {
            if (exportMessage !== "Exporting to Excel...") {
              setIsExportingToExcel(false);
              setExportMessage(ConstantValues.EMPTY_STRING);
            }
          }}
          onDismiss={() => {
            setIsExportingToExcel(false);
            setExportMessage(ConstantValues.EMPTY_STRING);
          }}
        />
      )}
    </>
  );
};
